import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';


type AppRole = 'admin' | 'reviewer' | 'viewer' | 'support' | 'compliance_analyst' | 'compliance_admin' | 'treasury' | 'finance_admin' | 'super_admin';

interface AdminUser {
  id: string;
  userId: string;
  email: string;
  fullName: string | null;
  roles: AppRole[];
  sessionTimeoutMinutes: number;
}

interface SignInResult {
  error: Error | null;
  user?: User | null;
}

interface AdminAuthContextType {
  user: User | null;
  session: Session | null;
  adminUser: AdminUser | null;
  adminUserLoaded: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  canEdit: boolean;
  canReview: boolean;
  /** Set true before MFA verify to suppress SIGNED_OUT from token rotation */
  setAuthGuard: (active: boolean) => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [adminUser, _setAdminUser] = useState<AdminUser | null>(null);
  const adminUserRef = useRef<AdminUser | null>(null);
  const setAdminUser = (val: AdminUser | null) => {
    adminUserRef.current = val;
    _setAdminUser(val);
  };
  const [adminUserLoaded, setAdminUserLoaded] = useState(false);

  // `loading` is ONLY for the initial bootstrap.
  const [loading, setLoading] = useState(true);
  const bootstrappedRef = useRef(false);

  // Guard: when signIn / MFA is in progress, ignore SIGNED_OUT events from
  // Supabase's internal session cleanup (token rotation).
  const signingInRef = useRef(false);

  // Track active performance session ID for heartbeats
  const perfSessionIdRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const perfSessionStartRef = useRef<number | null>(null);

  const startPerfSession = useCallback(async (accessToken: string) => {
    try {
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-session-tracker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
        body: JSON.stringify({ action: 'login', user_agent: navigator.userAgent }),
      });
      const data = await resp.json();
      if (data.perf_session_id) {
        perfSessionIdRef.current = data.perf_session_id;
        perfSessionStartRef.current = Date.now();
        if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = setInterval(async () => {
          if (!perfSessionIdRef.current) return;
          const { data: { session: s } } = await supabase.auth.getSession();
          if (!s) return;
          const elapsedSec = perfSessionStartRef.current
            ? Math.round((Date.now() - perfSessionStartRef.current) / 1000)
            : 0;
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-session-tracker`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s.access_token}` },
            body: JSON.stringify({ action: 'heartbeat', session_id: perfSessionIdRef.current, screen_time_seconds: elapsedSec }),
          }).catch(() => {});
        }, 60_000);
      }
    } catch (_) {}
  }, []);

  const endPerfSession = useCallback(async (accessToken?: string) => {
    if (heartbeatIntervalRef.current) { clearInterval(heartbeatIntervalRef.current); heartbeatIntervalRef.current = null; }
    if (!perfSessionIdRef.current) return;
    const token = accessToken || (await supabase.auth.getSession()).data.session?.access_token;
    if (!token) return;
    const elapsedSec = perfSessionStartRef.current
      ? Math.round((Date.now() - perfSessionStartRef.current) / 1000)
      : 0;
    try {
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-session-tracker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ action: 'logout', session_id: perfSessionIdRef.current, screen_time_seconds: elapsedSec }),
      });
    } catch (_) {}
    perfSessionIdRef.current = null;
    perfSessionStartRef.current = null;
  }, []);

  // Debounce SIGNED_OUT to handle token rotation
  const signOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track whether signOut was explicitly requested by user action
  const explicitSignOutRef = useRef(false);

  const fetchAdminUser = useCallback(async (userId: string, silent = false) => {
    if (!silent) setAdminUserLoaded(false);

    // Retry with exponential backoff to ride out transient network blips
    // (preview cold-starts, stale module HMR 502s, etc.).
    const withRetry = async <T,>(fn: () => Promise<T>, attempts = 3): Promise<T> => {
      let lastErr: unknown;
      for (let i = 0; i < attempts; i++) {
        try { return await fn(); } catch (e) {
          lastErr = e;
          if (i < attempts - 1) await new Promise(r => setTimeout(r, 250 * Math.pow(2, i)));
        }
      }
      throw lastErr;
    };

    try {
      const { data: adminData, error: adminError } = await withRetry(async () => await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle());

      if (adminError || !adminData) {
        console.error('User is not an admin:', adminError);
        setAdminUser(null);
        return;
      }

      const { data: rolesData, error: rolesError } = await withRetry(async () => await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId));

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
      }

      const roles = (rolesData?.map(r => r.role as AppRole) || []) as AppRole[];

      setAdminUser({
        id: adminData.id,
        userId: adminData.user_id,
        email: adminData.email,
        fullName: adminData.full_name,
        roles,
        sessionTimeoutMinutes: adminData.session_timeout_minutes ?? 5,
      });
    } catch (err) {
      console.error('Error fetching admin user:', err);
      setAdminUser(null);
    } finally {
      setAdminUserLoaded(true);
    }
  }, []);

  /**
   * Verify the Supabase session is truly dead before clearing local state.
   * Returns true if the session is gone (real sign-out), false if still alive.
   */
  const isSessionActuallyDead = useCallback(async (): Promise<boolean> => {
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      return !currentSession?.user;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      if (!isMounted) return;

      if (event === 'INITIAL_SESSION') return;

      if (event === 'SIGNED_OUT') {
        if (signingInRef.current) return;

        if (signOutTimerRef.current) {
          clearTimeout(signOutTimerRef.current);
          signOutTimerRef.current = null;
        }

        if (explicitSignOutRef.current) {
          explicitSignOutRef.current = false;
          setSession(null);
          setUser(null);
          setAdminUser(null);
          setAdminUserLoaded(true);
          return;
        }

        signOutTimerRef.current = setTimeout(async () => {
          if (!isMounted || signingInRef.current) return;

          const dead = await isSessionActuallyDead();
          if (!isMounted || signingInRef.current) return;

          if (dead) {
            setSession(null);
            setUser(null);
            setAdminUser(null);
            setAdminUserLoaded(true);
          }
        }, 2000);
        return;
      }

      if (signOutTimerRef.current) {
        clearTimeout(signOutTimerRef.current);
        signOutTimerRef.current = null;
      }

      setSession(nextSession);
      setUser(nextSession?.user ?? null);


      if (nextSession?.user) {
        const current = adminUserRef.current;
        if (!current || current.userId !== nextSession.user.id) {
          void fetchAdminUser(nextSession.user.id, !!current);
        }
      } else {
        setAdminUser(null);
        setAdminUserLoaded(true);
      }
    });

    const BROWSER_CLOSE_KEY = 'admin_session_active';
    const bootstrap = async () => {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (initialSession?.user) {
          setSession(initialSession);
          setUser(initialSession.user);
          localStorage.setItem(BROWSER_CLOSE_KEY, '1');
          await fetchAdminUser(initialSession.user.id);
          if (!perfSessionIdRef.current) {
            void startPerfSession(initialSession.access_token);
          }
        } else {
          setSession(null);
          setUser(null);
          setAdminUser(null);
          setAdminUserLoaded(true);
        }

        bootstrappedRef.current = true;
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const safetyTimeout = setTimeout(() => {
      if (isMounted && !bootstrappedRef.current) {
        console.warn('Admin auth bootstrap timed out – forcing redirect to login');
        setLoading(false);
        setAdminUserLoaded(true);
      }
    }, 8000);

    void bootstrap().finally(() => clearTimeout(safetyTimeout));

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
    };
  }, [fetchAdminUser, isSessionActuallyDead, endPerfSession, startPerfSession]);

  useEffect(() => {
    const handleUnload = () => { endPerfSession(); };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [endPerfSession]);

  const setAuthGuard = useCallback((active: boolean) => {
    signingInRef.current = active;
  }, []);

  const signIn = async (email: string, password: string): Promise<SignInResult> => {
    try {
      signingInRef.current = true;

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      signingInRef.current = false;

      if (error) {
        return { error: error as Error };
      }

      if (data?.session) {
        localStorage.setItem('admin_session_active', '1');
        setSession(data.session);
        setUser(data.user);
        void fetchAdminUser(data.user.id);
        void startPerfSession(data.session.access_token);
      }

      return { error: null, user: data?.user ?? null };
    } catch (err) {
      signingInRef.current = false;
      return { error: err as Error };
    }
  };

  const signOut = useCallback(async () => {
    if (signingInRef.current) {
      console.warn('[AdminAuth] signOut suppressed during auth guard');
      return;
    }
    explicitSignOutRef.current = true;
    localStorage.removeItem('admin_session_active');
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin.lastPath');
    }
    // Clear MFA verified flag so next login re-checks
    sessionStorage.removeItem('admin_mfa_verified');
    await endPerfSession();
    await supabase.auth.signOut();
    setAdminUser(null);
  }, [endPerfSession]);

  const hasRole = (role: AppRole) => {
    return adminUser?.roles.includes(role) ?? false;
  };

  const canEdit = hasRole('admin');
  const canReview = hasRole('admin') || hasRole('reviewer');

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        session,
        adminUser,
        adminUserLoaded,
        loading,
        signIn,
        signOut,
        hasRole,
        canEdit,
        canReview,
        setAuthGuard,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
